import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AuditIssueDetailModal, type AuditIssueDetailItem } from '../../components/AuditIssueDetailModal';
import { InfoTooltip } from '../../components/InfoTooltip';
import { supabase } from '../../lib/supabase';
import { functionErrorMessage } from '../../lib/functionError';
import { isLocked } from '../../lib/workspaces';
import { getWorkspaceBrandName } from '../../lib/brandName';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import type { Json } from '../../types/database';

type AnalysisStatus = 'good' | 'needs_improvement' | 'needs_fixing';
type AnalysisVerdict = 'pass' | 'partial' | 'fail';
type IssueSeverity = 'P1' | 'P2' | 'P3' | 'P4';

type AnalysisMetric = {
  label: string;
  value: string;
};

type AnalysisIssue = {
  severity: IssueSeverity;
  title: string;
  what: string;
  where?: string;
  why: string;
  howToFix: string;
  compliance?: string;
  auditedPaths?: string[];
  affectedPaths?: string[];
};

type AnalysisSection = {
  key: string;
  title: string;
  score?: number;
  status: AnalysisStatus;
  verdict?: AnalysisVerdict;
  summary: string;
  details: string[];
  metrics?: AnalysisMetric[];
  issues?: AnalysisIssue[];
  auditedPaths?: string[];
  partialPaths?: string[];
  failedPaths?: string[];
};

type AnalysisResult = {
  normalizedUrl: string;
  fetchedAt: string;
  overallScore: number;
  overallRating: 'Perfect' | 'Excellent' | 'Good' | 'OK' | 'Poor';
  statusCounts: Record<AnalysisStatus, number>;
  sections: AnalysisSection[];
};

type StoredAnalysisRow = {
  workspace_id: string;
  normalized_url: string;
  overall_score: number;
  fetched_at: string;
  next_allowed_run_at: string;
  analysis_result: Json;
};

type AuditJobStatus = 'queued' | 'running' | 'completed' | 'failed';

type StoredAuditJobRow = {
  id: string;
  workspace_id: string;
  normalized_url: string;
  status: AuditJobStatus;
  total_pages: number;
  processed_pages: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  finished_at: string | null;
};

const STALE_QUEUED_AUDIT_JOB_MS = 20 * 1000;
const STALE_RUNNING_AUDIT_JOB_MS = 90 * 1000;

type AuditCriteriaRow = {
  key: string;
  title: string;
  verdict: AnalysisVerdict;
  section: AnalysisSection;
};

type AuditRecommendationRow = {
  key: string;
  title: string;
  verdict: AnalysisVerdict;
  section: AnalysisSection;
  issue?: AnalysisIssue;
};

const WAND_SPARKLES_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72" />
    <path d="m14 7 3 3" />
    <path d="M5 6v4" />
    <path d="M19 14v4" />
    <path d="M10 2v2" />
    <path d="M7 8H3" />
    <path d="M21 16h-4" />
    <path d="M11 3H9" />
  </svg>
);

const DOWN_ARROW_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 18L12 22L16 18" />
    <path d="M12 2V22" />
  </svg>
);

const CHECK_ICON = (
  <svg width="13" height="8" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M18.5087 0.765625L6.93892 11.7051L2.10651 7.27186L0.59375 8.9177L6.19655 14.0582L6.96694 14.7656L7.72332 14.0513L20.0495 2.39744L18.5087 0.765625Z" fill="currentColor" />
  </svg>
);

const X_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const PARTIAL_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.1 2.182a10 10 0 0 1 3.8 0" />
    <path d="M13.9 21.818a10 10 0 0 1-3.8 0" />
    <path d="M17.609 3.721a10 10 0 0 1 2.69 2.7" />
    <path d="M2.182 13.9a10 10 0 0 1 0-3.8" />
    <path d="M20.279 17.609a10 10 0 0 1-2.7 2.69" />
    <path d="M21.818 10.1a10 10 0 0 1 0 3.8" />
    <path d="M3.721 6.391a10 10 0 0 1 2.7-2.69" />
    <path d="M6.391 20.279a10 10 0 0 1-2.69-2.7" />
  </svg>
);

const SECTION_CRITERIA_LABELS: Record<string, string> = {
  'site-files': 'Has a robots.txt that allows legitimate bot access',
  'discoverability-files': 'Provides an XML sitemap and discoverability files',
  'title-description': 'Has a proper page title and meta description',
  'canonical-urls': 'Uses canonical URLs consistently',
  'social-metadata': 'Has complete social metadata (Open Graph and Twitter)',
  'structured-data': 'Includes Schema.org JSON-LD structured data',
  'public-access': 'Does not block automated access with CAPTCHAs on public content',
  'url-readability': 'Uses clean, readable URLs',
  'api-spec': 'Publishes an OpenAPI/Swagger specification',
  'documentation': 'Has comprehensive, machine-readable documentation',
  'text-primary-content': 'Primary content is text-based (not locked in images/videos/PDFs)',
  'content-structure': 'Content structure is organised for scanning and summarisation',
  'html-semantics': 'Uses semantic HTML elements (header, nav, main, article, section, footer) instead of just divs',
  'image-accessibility': 'Images include useful alt text and accessibility metadata',
  'internal-linking': 'Internal links help crawlers and AI systems discover key pages',
  'link-health': 'Internal links resolve correctly without broken destinations',
  'javascript-rendering': 'Content is visible in HTML source, not just JS-rendered',
  'automation-readiness': 'Forms and interactive controls are machine-readable enough for agents',
};

const REPORT_HIGHLIGHT_KEYS = [
  'html-semantics',
  'title-description',
  'canonical-urls',
  'structured-data',
  'javascript-rendering',
] as const;

const SECTION_GROUPS: Array<{
  title: string;
  tooltip: string;
  keys: string[];
}> = [
  {
    title: 'Content and structure',
    tooltip: 'Checks covering page structure, metadata, schema, and whether core content is easy for AI systems to interpret.',
    keys: ['html-semantics', 'content-structure', 'text-primary-content', 'image-accessibility', 'title-description', 'canonical-urls', 'social-metadata', 'structured-data'],
  },
  {
    title: 'Discovery and crawlability',
    tooltip: 'Checks covering whether AI systems and search crawlers can access, understand, and discover key pages on your site.',
    keys: ['site-files', 'discoverability-files', 'public-access', 'url-readability', 'internal-linking', 'link-health', 'javascript-rendering'],
  },
  {
    title: 'Developer and agent readiness',
    tooltip: 'Checks covering documentation, API specifications, and whether your site is ready for agent and automation use cases.',
    keys: ['documentation', 'api-spec', 'automation-readiness'],
  },
];

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const formatDateOnly = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));

const formatRemainingCooldown = (value: string) => {
  const msRemaining = new Date(value).getTime() - Date.now();
  if (msRemaining <= 0) return 'now';

  const hoursRemaining = Math.max(1, Math.ceil(msRemaining / (60 * 60 * 1000)));
  return `${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'}`;
};

const isAnalysisResult = (value: Json | null): value is AnalysisResult => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.normalizedUrl === 'string' &&
    typeof candidate.fetchedAt === 'string' &&
    typeof candidate.overallScore === 'number' &&
    typeof candidate.overallRating === 'string' &&
    typeof candidate.statusCounts === 'object' &&
    Array.isArray(candidate.sections)
  );
};

const getSectionVerdict = (section: AnalysisSection): AnalysisVerdict => {
  if (section.verdict) return section.verdict;
  if (section.status === 'good') return 'pass';
  if (section.status === 'needs_improvement') return 'partial';
  return 'fail';
};

const getCriteriaLabel = (section: AnalysisSection) => SECTION_CRITERIA_LABELS[section.key] ?? section.title;

const getVerdictText = (verdict: AnalysisVerdict) =>
  verdict === 'pass' ? 'Pass' : verdict === 'partial' ? 'Partial' : 'Fail';

const getSeverityClassName = (verdict: AnalysisVerdict) =>
  verdict === 'pass' ? 'severity-label is-pass' : verdict === 'partial' ? 'severity-label is-medium' : 'severity-label';

const renderVerdictPill = (verdict: AnalysisVerdict) => (
  <div className={`${getSeverityClassName(verdict)} is-with-icon`}>
    {verdict === 'pass' ? (
      <div className="embed-icon w-embed">
        {CHECK_ICON}
      </div>
    ) : verdict === 'partial' ? (
      <div className="embed-icon is-orange-alert w-embed">
        {PARTIAL_ICON}
      </div>
    ) : verdict === 'fail' ? (
      <div className="embed-icon w-embed">
        {X_ICON}
      </div>
    ) : null}
    <div>{getVerdictText(verdict)}</div>
  </div>
);

const getRecommendationPriorityText = (recommendation: AuditRecommendationRow) => {
  if (recommendation.issue?.severity === 'P1') return 'Urgent';
  if (recommendation.issue?.severity === 'P2') return 'High';
  if (recommendation.issue?.severity === 'P3') return 'Medium';
  if (recommendation.issue?.severity === 'P4') return 'Low';
  if (recommendation.verdict === 'fail') return 'Urgent';
  if (recommendation.verdict === 'partial') return 'High';
  return 'Medium';
};

const getRecommendationPriorityClassName = (recommendation: AuditRecommendationRow) => {
  if (recommendation.issue?.severity === 'P1') return 'severity-label';
  if (recommendation.issue?.severity === 'P2') return 'severity-label is-high';
  if (recommendation.issue?.severity === 'P3') return 'severity-label is-medium';
  if (recommendation.issue?.severity === 'P4') return 'severity-label is-low';
  if (recommendation.verdict === 'fail') return 'severity-label';
  if (recommendation.verdict === 'partial') return 'severity-label is-medium';
  return 'severity-label is-medium';
};

const getCriteriaResultClassName = (verdict: AnalysisVerdict) => getSeverityClassName(verdict);

const createSectionDetailRow = (row: AuditCriteriaRow): AuditRecommendationRow => ({
  key: row.key,
  title: row.title,
  verdict: row.verdict,
  section: row.section,
  issue: undefined,
});

const formatAuditPathLabel = (path: string) => path === '/' ? 'index' : path;

const getDetailedFixes = (section: AnalysisSection, issue?: AnalysisIssue): string[] => {
  switch (issue?.title) {
    case 'robots.txt not found':
      return [
        'Create a plain text file called `robots.txt` at the root of your site so it loads at `https://yourdomain.com/robots.txt`.',
        'Add simple crawl rules for the public parts of the site and avoid blocking everything with a blanket `Disallow: /` rule.',
        'Reference your sitemap inside that file with a `Sitemap:` line, then publish and retest the URL in a browser.',
      ];
    case 'robots.txt blocks crawlers':
      return [
        'Open your existing `robots.txt` file and look for a wildcard rule like `User-agent: *` followed by `Disallow: /`.',
        'Replace that site-wide block with narrower rules that only block genuinely private paths such as account or admin areas.',
        'Republish the file and confirm your important public pages are no longer disallowed.',
      ];
    case 'llms.txt not found':
      return [
        'Create a plain text file called `llms.txt` at the root of the site so it loads at `https://yourdomain.com/llms.txt`.',
        'Use it to describe the site, key sections, and any preferred documentation or AI-facing resources in plain language.',
        'Keep it short, factual, and easy to maintain as your product or site structure changes.',
      ];
    case 'XML sitemap not found':
      return [
        'Generate a `sitemap.xml` file that lists the important public URLs you want crawlers and AI systems to discover.',
        'Make sure the URLs in the sitemap are the preferred canonical versions, not duplicate or parameter-heavy versions.',
        'Publish the file at `/sitemap.xml` and reference it from `robots.txt`.',
      ];
    case 'Sitemap appears empty or invalid':
      return [
        'Open the sitemap and confirm it contains valid `<urlset>` or `<sitemapindex>` XML rather than an empty or malformed file.',
        'Regenerate the sitemap from your CMS, framework, or SEO plugin if the structure is broken.',
        'Validate the file and confirm it contains the main public canonical URLs you want indexed.',
      ];
    case 'Page title missing':
      return [
        'Add a `<title>` tag to the page template so every important page outputs a descriptive page title.',
        'Write the title to clearly state what the page is about in human language, not just the brand name.',
        'Check a few live pages to make sure each one has a unique title rather than a duplicated site-wide default.',
      ];
    case 'Page title length needs attention':
      return [
        'Review the current title and remove filler words, repeated branding, or long boilerplate text.',
        'Aim for a short, specific title that explains the page topic clearly in roughly 30 to 70 characters.',
        'Apply the same title-writing rule across key templates so new pages stay consistent.',
      ];
    case 'Meta description missing':
      return [
        'Add a meta description field to the page template or CMS entry for this page type.',
        'Write one or two plain-language sentences that summarise what the page offers and who it is for.',
        'Keep the description concise and useful rather than stuffing keywords or repeating the title.',
      ];
    case 'Canonical URL not found':
      return [
        'Add a `<link rel=\"canonical\">` tag to the page head so the preferred URL is declared explicitly.',
        'Point the canonical tag at the clean public version of the page, not a staging, tracking, or parameter-heavy URL.',
        'Check a few page templates to make sure each canonical updates dynamically to the correct page URL.',
      ];
    case 'Open Graph metadata incomplete':
      return [
        'Add `og:title` and `og:description` to the page head so shared links and machine-read previews have a clear summary.',
        'Add an `og:image` where possible so the page has a complete preview card rather than text-only metadata.',
        'Use the same content source or template logic across pages so these values stay in sync with the page purpose.',
      ];
    case 'Twitter card metadata missing':
      return [
        'Add `twitter:card` to the page head, usually with a summary or summary-large-image card type.',
        'Add matching Twitter title and description tags, or ensure your social metadata fallback logic is working cleanly.',
        'Check a few public pages to confirm the tags are present consistently across the site.',
      ];
    case 'Structured data not found':
      return [
        'Choose the Schema.org type that best matches the page, such as `Organization`, `Article`, `Product`, or `FAQPage`.',
        'Add JSON-LD in the page head or body with the fields that genuinely describe the page content.',
        'Validate the markup and roll it out first on your most important templates before expanding site-wide.',
      ];
    case 'Structured data contains invalid JSON-LD':
      return [
        'Inspect the JSON-LD script block and check for syntax errors such as missing commas, quotes, or broken nesting.',
        'Validate the markup with a structured data tester until the JSON parses cleanly.',
        'Then confirm the property names and values still match real page content rather than placeholder data.',
      ];
    case 'Semantic HTML usage is low':
      return [
        'Review the page layout and replace generic containers with meaningful elements like `header`, `main`, `article`, `section`, and `footer`.',
        'Use those semantic elements to reflect the real structure of the page, not just for styling.',
        'Apply the same improvements in shared layout components so the fix carries across templates.',
      ];
    case 'Some images are missing useful alt text':
      return [
        'List the informative images on the page and add short alt text that explains what each one contributes.',
        'Leave decorative images empty rather than forcing descriptive text onto images that do not matter.',
        'If images come from a CMS, add an alt text field to the editor workflow so it is not missed again.',
      ];
    case 'No meaningful internal links detected':
      return [
        'Add links from the page to closely related pages, supporting resources, and key navigation destinations.',
        'Use descriptive link text so both people and crawlers understand what the destination page is about.',
        'Prioritise links to your most important product, documentation, and conversion pages first.',
      ];
    case 'Broken internal links':
      return [
        'Open the affected URLs and identify which links return errors, redirect incorrectly, or point to removed pages.',
        'Update those links to the correct live destination or remove them if the page no longer exists.',
        'After fixing the links, test the destinations again to confirm they return a healthy public response.',
      ];
    case 'Primary content looks heavily JS-rendered':
      return [
        'Check whether the important page copy is missing from the initial HTML and only appears after client-side JavaScript runs.',
        'Move the critical headings, text, and metadata into server-rendered HTML where possible.',
        'If full server rendering is not practical, at least ensure the most important content is included in the first HTML response.',
      ];
    case 'Important content may rely partly on JavaScript':
      return [
        'Open the raw page source for the affected page and check whether the main heading, supporting copy, and key CTAs are already present before scripts run.',
        'If the HTML is too light, move that important content into the server-rendered response instead of waiting for JavaScript to inject it.',
        'Retest the page source after changes and aim for a clear headline plus enough supporting text to explain the page without relying on client-side rendering.',
      ];
    case 'Public content appears challenge-gated':
      return [
        'Identify which public pages are showing CAPTCHA, bot-check, or challenge screens to normal crawlers.',
        'Remove those gates from the public pages you want discovered, or configure exceptions for safe crawler access.',
        'Retest the page from a clean browser session and confirm the HTML content loads without a challenge step.',
      ];
    case 'OpenAPI or Swagger specification not found':
      return [
        'If your product exposes an API, publish a machine-readable spec such as OpenAPI or Swagger at a stable public route.',
        'Use a conventional path like `/openapi.json` or `/swagger.json` so tools can discover it easily.',
        'Keep that spec updated when endpoints or request formats change so it stays trustworthy.',
      ];
    case 'Documentation hub not found':
      return [
        'Publish a documentation landing area that explains how the product works and links to the key reference pages.',
        'Put it on a predictable public route such as `/docs`, `/documentation`, or `/developers`.',
        'Structure it clearly with headings, navigation, and linkable sections so both humans and tools can scan it easily.',
      ];
    case 'Primary content is light on crawlable text':
      return [
        'Move the most important information into normal HTML headings, paragraphs, and lists rather than relying on images, PDFs, or embedded media.',
        'Make sure each important page can be understood from its text content alone.',
        'Start with the homepage and top commercial or documentation pages, then expand the same pattern elsewhere.',
      ];
    case 'Programmatic Label Association':
      return [
        'Add a proper `<label>` for each input field and connect it to the field using the matching `for` and `id` attributes.',
        'Where a visible label is not practical, add `aria-label` or `aria-labelledby` so the control still has a machine-readable name.',
        'Test the main forms again and confirm every field has a clear accessible label.',
      ];
    case 'Standard autocomplete usage':
      return [
        'Review the main forms and add standard `autocomplete` values to common fields such as name, email, company, phone, and URL.',
        'Use the standard token that matches the field purpose rather than custom or invented values.',
        'Retest the form HTML to confirm those attributes are present on the actual input elements.',
      ];
    case 'Page could not be fetched':
      return [
        'Open the page directly and confirm it returns a stable public HTML response rather than an error, timeout, or block page.',
        'Check redirects, SSL, bot protection, and hosting rules to make sure the page is reachable from outside your own browser session.',
        'Once the page loads reliably, rerun the audit so the rest of the checks can complete properly.',
      ];
    default:
      break;
  }

  switch (section.key) {
    case 'site-files':
      return [
        'Make sure `robots.txt` is live at the site root so search engines and AI tools know what public content they can access.',
        'If `llms.txt` is missing, add it with a short plain-English summary of your site and links to the pages you most want AI tools to use.',
        'Treat extra files like `security.txt`, `ai-plugin.json`, or `mcp.json` as optional unless you have a specific product or integration that needs them.',
      ];
    case 'discoverability-files':
      return [
        'Publish a valid `sitemap.xml` file that lists the main public canonical URLs you want discovered.',
        'Keep it automatically updated as new important pages are added or removed.',
        'Reference the sitemap from `robots.txt` so crawlers can find it quickly.',
      ];
    case 'title-description':
      return [
        'Ensure every important template outputs a unique page title and meta description.',
        'Write both in plain language so the page purpose is obvious at a glance.',
        'Spot-check several live pages to confirm the values are not duplicated or missing.',
      ];
    case 'canonical-urls':
      return [
        'Add canonical tags to important page templates so each page declares its preferred source URL.',
        'Make sure the canonical points to the clean public URL, not a duplicate or tracked version.',
        'Test a few representative pages to confirm the canonical value changes correctly from page to page.',
      ];
    case 'social-metadata':
      return [
        'Add Open Graph and Twitter metadata to your main templates so shared previews and machine-read summaries stay consistent.',
        'Include title, description, and ideally an image for important commercial or content pages.',
        'Use one shared metadata pattern across templates so it is easier to maintain.',
      ];
    case 'structured-data':
      return [
        'Choose the right Schema.org type for each important page type and add JSON-LD markup to match.',
        'Start with the homepage and the page types that matter most for search visibility or product understanding.',
        'Validate the output regularly so broken structured data does not accumulate unnoticed.',
      ];
    case 'public-access':
      return [
        'Identify any public pages that trigger challenge screens, bot checks, or access blocks.',
        'Remove those barriers from pages you want discovered by search engines and AI systems.',
        'Retest from an anonymous session to confirm the HTML loads cleanly.',
      ];
    case 'url-readability':
      return [
        'Shorten overly long or messy URLs and remove unnecessary query parameters from canonical page versions.',
        'Use descriptive slugs that reflect the page topic in normal language.',
        'Apply the same URL pattern consistently across similar page types.',
      ];
    case 'api-spec':
      return [
        'Publish a machine-readable API specification if your product exposes an API or agent integration surface.',
        'Use a stable public route and keep it versioned or updated as the API changes.',
        'Link to that spec from your documentation so it is easy to discover.',
      ];
    case 'documentation':
      return [
        'Create a clear documentation hub with structured navigation and linkable sections.',
        'Put it on a predictable route such as `/docs` or `/developers`.',
        'Cover the topics a new user or agent would need first: what the product does, how it works, and how to integrate with it.',
      ];
    case 'text-primary-content':
      return [
        'Move important information out of images, PDFs, or embedded assets and into normal HTML text.',
        'Use headings and short paragraphs so the page can be understood from the text alone.',
        'Start with your highest-value pages first and expand from there.',
      ];
    case 'content-structure':
      return [
        'Break long walls of text into clear sections with descriptive headings.',
        'Use lists, short paragraphs, and logical grouping so the page is easy to scan and summarise.',
        'Apply the same structure rules across the core templates rather than fixing pages one by one.',
      ];
    case 'html-semantics':
      return [
        'Replace generic containers with semantic page landmarks where appropriate.',
        'Use `main`, `section`, `article`, and related elements to reflect the real page structure.',
        'Roll those changes into shared layout components so the improvement is site-wide.',
      ];
    case 'image-accessibility':
      return [
        'Add useful alt text to informative images and leave decorative images empty.',
        'Make alt text part of the content workflow so it is filled in when images are added.',
        'Audit the most visible templates first, such as homepage, product, and article pages.',
      ];
    case 'internal-linking':
      return [
        'Add clear links between related pages so important content is easier to discover.',
        'Use descriptive link text instead of vague phrases like “click here”.',
        'Prioritise links to your most important conversion, product, and documentation pages.',
      ];
    case 'link-health':
      return [
        'Audit the internal links on your main pages and identify any destinations that error or redirect badly.',
        'Update broken destinations to the correct live URLs.',
        'Rerun checks periodically so regressions are caught quickly.',
      ];
    case 'javascript-rendering':
      return [
        'Ensure the most important content appears in the initial HTML response and is not dependent on JavaScript execution.',
        'Server-render key copy and metadata where possible.',
        'Retest with page source, not just the rendered browser view, to confirm the improvement.',
      ];
    case 'automation-readiness':
      return [
        'Review your main forms and interactive controls for clear labels, predictable names, and standard HTML semantics.',
        'Add standard autocomplete values where appropriate so tools can understand common fields.',
        'Test the important forms again with accessibility and HTML inspection tools to confirm the markup is machine-readable.',
      ];
    default:
      return ['Review the detailed audit evidence and apply the most direct fix on the affected pages first.'];
  }
};

const getResultCardVariantClass = (rating: AnalysisResult['overallRating']) => {
  if (rating === 'Perfect') return 'small-card is-audit-result is-perfect';
  if (rating === 'Excellent') return 'small-card is-audit-result';
  if (rating === 'Good') return 'small-card is-audit-result is-light-green';
  if (rating === 'OK') return 'small-card is-audit-result is-orange';
  return 'small-card is-audit-result is-red';
};

const getDisplayHost = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const isAuditJobActive = (status: AuditJobStatus | null | undefined) =>
  status === 'queued' || status === 'running';

const isAuditJobStale = (job: StoredAuditJobRow | null) => {
  if (!job || !isAuditJobActive(job.status)) return false;
  const updatedAtMs = new Date(job.updated_at).getTime();
  if (Number.isNaN(updatedAtMs)) return false;
  const staleThreshold = job.status === 'queued' ? STALE_QUEUED_AUDIT_JOB_MS : STALE_RUNNING_AUDIT_JOB_MS;
  return Date.now() - updatedAtMs > staleThreshold;
};

export function AIAnalysis() {
  const { workspace } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceBrandName = getWorkspaceBrandName(workspace);
  const workspaceId = workspace?.id ?? null;
  const brandUrl = workspace?.brand_url?.trim() || '';
  const hasTriggeredAutorunRef = useRef(false);
  const hasAttemptedAuditRunRef = useRef(false);
  const latestAuditAttemptAtRef = useRef<number | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const breakdownRef = useRef<HTMLDivElement | null>(null);
  const [selectedRecommendation, setSelectedRecommendation] = useState<AuditRecommendationRow | null>(null);

  const storedAnalysisQuery = useQuery({
    queryKey: ['workspace-ai-analysis', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<StoredAnalysisRow | null> => {
      const { data, error } = await supabase
        .from('workspace_ai_analyses')
        .select('workspace_id, normalized_url, overall_score, fetched_at, next_allowed_run_at, analysis_result')
        .eq('workspace_id', workspaceId!)
        .maybeSingle();

      if (error) throw error;
      return (data as StoredAnalysisRow | null) ?? null;
    },
  });

  const latestAuditJobQuery = useQuery({
    queryKey: ['workspace-ai-audit-job', workspaceId],
    enabled: Boolean(workspaceId),
    refetchInterval: (query) => {
      const data = query.state.data as StoredAuditJobRow | null | undefined;
      return isAuditJobActive(data?.status) ? 2000 : false;
    },
    queryFn: async (): Promise<StoredAuditJobRow | null> => {
      const { data, error } = await supabase
        .from('workspace_ai_audit_jobs')
        .select('id, workspace_id, normalized_url, status, total_pages, processed_pages, error_message, created_at, updated_at, started_at, finished_at')
        .eq('workspace_id', workspaceId!)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return (data as StoredAuditJobRow | null) ?? null;
    },
  });

  const completedAuditRunCountQuery = useQuery({
    queryKey: ['workspace-ai-audit-completed-count', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase
        .from('workspace_ai_audit_jobs')
        .select('id')
        .eq('workspace_id', workspaceId!)
        .eq('status', 'completed')
        .limit(2);

      if (error) throw error;
      return data?.length ?? 0;
    },
  });

  const runAnalysisMutation = useMutation({
    onMutate: () => {
      hasAttemptedAuditRunRef.current = true;
      latestAuditAttemptAtRef.current = Date.now();
      if (workspaceId && brandUrl) {
        queryClient.setQueryData<StoredAuditJobRow | null>(['workspace-ai-audit-job', workspaceId], {
          id: `pending-${Date.now()}`,
          workspace_id: workspaceId,
          normalized_url: brandUrl,
          status: 'queued',
          total_pages: 0,
          processed_pages: 0,
          error_message: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          started_at: null,
          finished_at: null,
        });
      }
    },
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('analyze-ai-readiness', {
        body: { workspace_id: workspaceId, url: brandUrl },
      });
      if (error) throw new Error(await functionErrorMessage(error));
      return data as {
        cached?: boolean;
        queued?: boolean;
        jobId?: string;
        status?: AuditJobStatus;
        processedPages?: number;
        totalPages?: number;
        nextAllowedRunAt?: string;
        result?: AnalysisResult;
      };
    },
    onSuccess: async (payload) => {
      if (workspaceId && payload?.jobId && payload?.status) {
        queryClient.setQueryData<StoredAuditJobRow | null>(['workspace-ai-audit-job', workspaceId], {
          id: payload.jobId,
          workspace_id: workspaceId,
          normalized_url: brandUrl,
          status: payload.status,
          total_pages: payload.totalPages ?? 0,
          processed_pages: payload.processedPages ?? 0,
          error_message: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          started_at: payload.status === 'running' ? new Date().toISOString() : null,
          finished_at: null,
        });
      }

      if (payload?.result && workspaceId) {
        queryClient.setQueryData<StoredAnalysisRow | null>(['workspace-ai-analysis', workspaceId], {
          workspace_id: workspaceId,
          normalized_url: payload.result.normalizedUrl,
          overall_score: payload.result.overallScore,
          fetched_at: payload.result.fetchedAt,
          next_allowed_run_at: payload.nextAllowedRunAt ?? storedAnalysisQuery.data?.next_allowed_run_at ?? new Date().toISOString(),
          analysis_result: payload.result as Json,
        });
      }

      await queryClient.refetchQueries({ queryKey: ['workspace-ai-audit-job', workspaceId], exact: true });
      await queryClient.refetchQueries({ queryKey: ['workspace-ai-analysis', workspaceId], exact: true });
      await queryClient.refetchQueries({ queryKey: ['workspace-ai-audit-completed-count', workspaceId], exact: true });
      if (payload?.cached && payload.nextAllowedRunAt) {
        toast.info(`AI Site Audit can be re-run after ${formatDateTime(payload.nextAllowedRunAt)}.`);
      }
    },
  });

  const storedResult = useMemo(() => {
    const raw = storedAnalysisQuery.data?.analysis_result ?? null;
    return isAnalysisResult(raw) ? raw : null;
  }, [storedAnalysisQuery.data?.analysis_result]);

  const liveMutationResult = useMemo(() => {
    const raw = runAnalysisMutation.data?.result;
    return raw && isAnalysisResult(raw as Json) ? (raw as AnalysisResult) : null;
  }, [runAnalysisMutation.data?.result]);

  const displayResult = liveMutationResult ?? storedResult;
  const latestAuditJob = latestAuditJobQuery.data ?? null;
  const latestAuditJobCreatedAtMs = latestAuditJob?.created_at ? new Date(latestAuditJob.created_at).getTime() : NaN;
  const latestAuditAttemptAtMs = latestAuditAttemptAtRef.current;
  const isLatestJobFromCurrentAttempt = latestAuditJob?.id?.startsWith('pending-')
    || latestAuditAttemptAtMs === null
    || !Number.isNaN(latestAuditJobCreatedAtMs) && latestAuditJobCreatedAtMs >= latestAuditAttemptAtMs - 1000;
  const isLatestAuditJobStale = isAuditJobStale(latestAuditJob);
  const isAuditRunning = runAnalysisMutation.isPending || (isAuditJobActive(latestAuditJob?.status) && !isLatestAuditJobStale);
  const isResolvingStoredAudit = Boolean(
    brandUrl && !displayResult && storedAnalysisQuery.isLoading && !isAuditRunning
  );
  const nextAllowedRunAt = storedAnalysisQuery.data?.next_allowed_run_at ?? null;
  const completedAuditRunCount = Math.max(
    completedAuditRunCountQuery.data ?? 0,
    storedAnalysisQuery.data?.fetched_at ? 1 : 0,
  );
  const canUseImmediateAuditRerun = Boolean(displayResult) && completedAuditRunCount < 2;
  const canRunAnalysis = !isAuditRunning && (
    canUseImmediateAuditRerun ||
    !nextAllowedRunAt ||
    new Date(nextAllowedRunAt).getTime() <= Date.now()
  );
  const rerunCooldownLabel = nextAllowedRunAt ? formatRemainingCooldown(nextAllowedRunAt) : null;
  const auditHost = getDisplayHost(displayResult?.normalizedUrl || brandUrl);
  const runningProgressLabel =
    latestAuditJob && latestAuditJob.total_pages > 0
      ? `${Math.min(latestAuditJob.processed_pages, latestAuditJob.total_pages)} of ${latestAuditJob.total_pages}`
      : null;
  const auditRunStateLabel =
    latestAuditJob?.status === 'running'
      ? 'Audit running'
      : latestAuditJob?.status === 'queued' || runAnalysisMutation.isPending
        ? 'Audit queued'
        : null;
  const analysisErrorMessage =
    isLatestAuditJobStale
      ? 'This audit stopped updating before it completed. Please run the audit again.'
      : latestAuditJob?.status === 'failed' && latestAuditJob.error_message
      ? latestAuditJob.error_message
      : runAnalysisMutation.error instanceof Error
        ? runAnalysisMutation.error.message
        : storedAnalysisQuery.error instanceof Error
          ? storedAnalysisQuery.error.message
          : 'Something went wrong while analysing this site.';
  const shouldShowInlineAuditFailure = !displayResult && hasAttemptedAuditRunRef.current && (
    !!runAnalysisMutation.error ||
    !!storedAnalysisQuery.error ||
    (isLatestJobFromCurrentAttempt && latestAuditJob?.status === 'failed') ||
    (isLatestJobFromCurrentAttempt && isLatestAuditJobStale)
  );

  const criteriaRows = useMemo<AuditCriteriaRow[]>(
    () =>
      (displayResult?.sections ?? []).map((section) => ({
        key: section.key,
        title: getCriteriaLabel(section),
        verdict: getSectionVerdict(section),
        section,
      })),
    [displayResult]
  );

  const passCount = criteriaRows.filter((row) => row.verdict === 'pass').length;

  const recommendationRows = useMemo<AuditRecommendationRow[]>(
    () =>
      criteriaRows
        .flatMap<AuditRecommendationRow>((row) => {
          if (row.section.issues && row.section.issues.length > 0) {
            return row.section.issues.map((issue, index) => ({
              key: `${row.key}-issue-${index}`,
              title: issue.title,
              verdict: issue.severity === 'P1' ? 'fail' : 'partial',
              section: row.section,
              issue,
            }));
          }

          if (row.verdict === 'pass') return [];

          return [{
            key: row.key,
            title: row.title,
            verdict: row.verdict,
            section: row.section,
            issue: undefined,
          }];
        })
        .sort((left, right) => {
          const leftRank = left.verdict === 'fail' ? 0 : 1;
          const rightRank = right.verdict === 'fail' ? 0 : 1;
          return leftRank - rightRank;
        }),
    [criteriaRows]
  );

  const priorityRecommendationCount = useMemo(
    () => recommendationRows.filter((row) => getRecommendationPriorityText(row) !== 'Urgent').length,
    [recommendationRows]
  );

  const urgentRecommendationCount = useMemo(
    () => recommendationRows.filter((row) => getRecommendationPriorityText(row) === 'Urgent').length,
    [recommendationRows]
  );

  const selectedRecommendationItem = useMemo<AuditIssueDetailItem | null>(() => {
    if (!selectedRecommendation) return null;

    const { section, issue } = selectedRecommendation;
    const isSectionDetail = !issue;
    const sectionAffectedPaths = Array.from(
      new Set(section.issues?.flatMap((sectionIssue) => sectionIssue.affectedPaths ?? []) ?? [])
    );

    const fixes = getDetailedFixes(section, issue);

    const auditedPaths = Array.from(
      new Set([
        ...(issue?.auditedPaths ?? []),
        ...(section.auditedPaths ?? []),
      ])
    );

    const affectedPaths = Array.from(
      new Set(isSectionDetail ? sectionAffectedPaths : (issue?.affectedPaths ?? []))
    );
    const partialPaths = Array.from(new Set(section.partialPaths ?? []));
    const failedPaths = Array.from(new Set(section.failedPaths ?? []));
    const affectedPathSummary = !isSectionDetail && affectedPaths.length > 0
      ? affectedPaths.length === 1
        ? `Affected page: ${formatAuditPathLabel(affectedPaths[0])}.`
        : `Affected pages include: ${affectedPaths.slice(0, 3).map(formatAuditPathLabel).join(', ')}.`
      : null;
    const whatItMeans = [
      affectedPathSummary,
      issue?.what,
      issue?.why,
      section.summary,
      ...section.details,
      issue?.where ? `HTML evidence: ${issue.where}` : null,
    ].filter((value): value is string => Boolean(value));

    return {
      key: selectedRecommendation.key,
      title: selectedRecommendation.title,
      resultLabel: isSectionDetail
        ? getVerdictText(selectedRecommendation.verdict)
        : getRecommendationPriorityText(selectedRecommendation),
      resultClassName: isSectionDetail
        ? getCriteriaResultClassName(selectedRecommendation.verdict)
        : getRecommendationPriorityClassName(selectedRecommendation),
      whatItMeans,
      fixes,
      auditedPaths,
      affectedPaths,
      partialPaths: isSectionDetail ? partialPaths : [],
      failedPaths: isSectionDetail ? failedPaths : [],
      evidence: section.metrics?.map((metric) => `${metric.label}: ${metric.value}`) ?? [],
      compliance: issue?.compliance ?? null,
    };
  }, [selectedRecommendation]);

  const reportRows = useMemo(
    () =>
      REPORT_HIGHLIGHT_KEYS.map((key) => {
        const existing = criteriaRows.find((row) => row.key === key);
        if (existing) return existing;

        return {
          key,
          title: SECTION_CRITERIA_LABELS[key] ?? key,
          verdict: 'fail' as AnalysisVerdict,
          section: {
            key,
            title: SECTION_CRITERIA_LABELS[key] ?? key,
            score: 0,
            status: 'needs_fixing',
            verdict: 'fail',
            summary: 'This criterion was not present in the stored audit result.',
            details: ['Run a fresh audit to populate the latest score overview fields for this criterion.'],
            issues: [],
          },
        } satisfies AuditCriteriaRow;
      }),
    [criteriaRows]
  );

  const groupedRows = useMemo(
    () =>
      SECTION_GROUPS.map((group) => ({
        ...group,
        rows: group.keys
          .map((key) => criteriaRows.find((row) => row.key === key))
          .filter(Boolean) as AuditCriteriaRow[],
      })).filter((group) => group.rows.length > 0),
    [criteriaRows]
  );

  const runAnalysis = async () => {
    if (!brandUrl || !workspaceId || isLocked(workspace)) return;
    if (!canRunAnalysis) {
      toast.info(nextAllowedRunAt
        ? `AI Site Audit can be re-run after ${formatDateTime(nextAllowedRunAt)}`
        : 'AI Site Audit cannot be re-run yet.'
      );
      return;
    }
    await runAnalysisMutation.mutateAsync();
  };

  useEffect(() => {
    const shouldAutorun = searchParams.get('autorun') === '1';
    if (!shouldAutorun || hasTriggeredAutorunRef.current || !brandUrl || !workspaceId) return;

    hasTriggeredAutorunRef.current = true;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('autorun');
    setSearchParams(nextParams, { replace: true });
    void runAnalysis();
  }, [searchParams, setSearchParams, brandUrl, workspaceId]);

  useEffect(() => {
    if (!workspaceId || !latestAuditJob) return;
    if (latestAuditJob.status === 'completed') {
      void queryClient.refetchQueries({ queryKey: ['workspace-ai-analysis', workspaceId], exact: true });
      void queryClient.refetchQueries({ queryKey: ['workspace-ai-audit-completed-count', workspaceId], exact: true });
      void queryClient.invalidateQueries({ queryKey: ['sidebar_insights_count', workspaceId] });
    }
  }, [latestAuditJob?.id, latestAuditJob?.status, workspaceId, queryClient]);

  return (
    <div>
      <div className="spacer-xsmall"></div>

      <div className="page-header-wrapper">
        <h1>{displayResult ? 'Audit results' : ''}</h1>
        {brandUrl && displayResult ? (
          <button
            type="button"
            className="button is-small w-button"
            onClick={runAnalysis}
            disabled={isAuditRunning || !canRunAnalysis}
            title={!canRunAnalysis && nextAllowedRunAt ? `Available again after ${formatDateTime(nextAllowedRunAt)}` : undefined}
          >
            {isAuditRunning
              ? 'Running audit...'
              : !canRunAnalysis && rerunCooldownLabel
                ? `Run site audit (${rerunCooldownLabel})`
                : displayResult
                  ? 'Re-run audit'
                  : 'Run site audit'}
          </button>
        ) : null}
      </div>

      {!brandUrl ? (
        <div className="dashboard-panel ai-analysis-empty-panel">
          <div className="dashboard-panel-body ai-analysis-empty-body">
            <div className="dashboard-panel-title">Add your website URL first</div>
            <div className="text-size-small text-colour-alt-grey">
              AI Site Audit needs a workspace website URL before it can audit AEO readiness.
            </div>
            <div className="spacer-small"></div>
            <Link to="/overview" className="button has-inner-shadow w-button">
              Go to Overview
            </Link>
          </div>
        </div>
      ) : isResolvingStoredAudit ? (
        <div className="dashboard-panel">
          <div className="dashboard-panel-body ai-analysis-loading-body">
            <div className="loading-spinner" />
            <div className="text-size-small text-colour-alt-grey">Loading your latest AI audit...</div>
          </div>
        </div>
      ) : shouldShowInlineAuditFailure ? (
        <div className="dashboard-panel">
          <div className="dashboard-panel-body ai-analysis-empty-body">
            <div className="dashboard-panel-title">Analysis failed</div>
            <div className="text-size-small text-colour-alt-grey">
              {analysisErrorMessage}
            </div>
            <div className="spacer-small"></div>
            <button
              type="button"
              className="button is-small is-white w-button"
              onClick={runAnalysis}
              disabled={isAuditRunning || !canRunAnalysis}
            >
              Try again
            </button>
          </div>
        </div>
      ) : isAuditRunning ? (
        <div className="ai-analysis-start-shell">
          <div className="dashboard-panel ai-analysis-empty-panel is-start-state">
            <div className="dashboard-panel-body ai-analysis-empty-body ai-analysis-start-body">
              <div className="ai-analysis-start-icon-wrap">
                <div className="ai-analysis-start-icon">{WAND_SPARKLES_ICON}</div>
              </div>
              <div className="spacer-tiny"></div>
              <div
                className="text-size-medium text-weight-semibold text-colour-white ai-analysis-start-title is-shimmer"
                data-text="Running site audit"
              >
                Running site audit
              </div>
              <div className="text-size-regular text-colour-opacity-80 ai-analysis-start-copy">
                {`${auditRunStateLabel || 'Audit queued'}${runningProgressLabel ? ` • Progress: ${runningProgressLabel}.` : '.'} We're running a comprehensive site audit, this may take a few minutes.`}
              </div>
              <div className="spacer-small"></div>
              <button
                type="button"
                className="button is-white w-button is-button-flex"
                disabled
              >
                <span className="ai-analysis-button-spinner" aria-hidden="true" />
                <span>Running audit</span>
              </button>
            </div>
          </div>
        </div>
      ) : !displayResult ? (
        <div className="ai-analysis-start-shell">
          <div className="dashboard-panel ai-analysis-empty-panel is-start-state">
            <div className="dashboard-panel-body ai-analysis-empty-body ai-analysis-start-body">
              <div className="ai-analysis-start-icon-wrap">
                <div className="ai-analysis-start-icon">{WAND_SPARKLES_ICON}</div>
              </div>
              <div className="spacer-tiny"></div>
              <div
                className={`text-size-medium text-weight-semibold text-colour-white${isAuditRunning ? ' ai-analysis-start-title is-shimmer' : ''}`}
                data-text={isAuditRunning ? 'Running site audit' : undefined}
              >
                {isAuditRunning ? 'Running site audit' : 'Run free site audit'}
              </div>
              <div className="text-size-regular text-colour-opacity-80 ai-analysis-start-copy">
                {isAuditRunning
                  ? `${auditRunStateLabel || 'Audit queued'}${runningProgressLabel ? ` • Progress: ${runningProgressLabel}.` : '.'} We're running a comprehensive site audit, this may take a few minutes.`
                  : `We’ll check how readable, crawlable, and automation-friendly ${workspaceBrandName} is for AI search engines and agents.`}
              </div>
              <div className="spacer-small"></div>
              <button
                type="button"
                className="button is-white w-button is-button-flex"
                onClick={runAnalysis}
                disabled={isAuditRunning}
              >
                {isAuditRunning ? (
                  <>
                    <span className="ai-analysis-button-spinner" aria-hidden="true" />
                    <span>Running audit</span>
                  </>
                ) : (
                  'Run audit now'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid-wrapper">
          <div className="audit-result-top">
            <div className="audit-left-wrapper">
              <div className="card is-audit-result-header">
                <div className={getResultCardVariantClass(displayResult.overallRating)}>
                  <div className="audit-result-box">
                    <div
                      className="score-circle"
                      style={{ ['--audit-progress' as string]: `${Math.max(0, Math.min(100, displayResult.overallScore)) * 3.6}deg` }}
                    >
                      <div>{displayResult.overallScore}</div>
                    </div>
                    <div className="text-size-large text-weight-bold">
                      {displayResult.overallRating} score for <br />
                      {workspaceBrandName}
                    </div>
                  </div>
                  <div className="spacer-small"></div>
                  <div className="insight-body">
                    <div className="text-size-regular">
                      <span className="text-weight-semibold">
                        We&apos;ve checked {criteriaRows.length} core criteria to assess {auditHost} for AI search compatibility.
                      </span>
                      <br />
                      <br />
                      Explore the full breakdown below to see what&apos;s working well, what needs attention, and where to focus next. After the first audit, you can re-run it once immediately. After that, you can run one audit every 24 hours.
                    </div>
                    <div className="spacer-small"></div>
                    <button
                      type="button"
                      className="button is-white is-button-flex w-inline-block"
                      onClick={() => breakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    >
                      <div>See full breakdown</div>
                      <div className="embed-icon w-embed">{DOWN_ARROW_ICON}</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="recommendation-wrapper">
              <div className="card is-audit-card-header">
                <div className="small-card is-audit-results">
                  <div className="audit-title-top">
                    <div className="text-size-regular text-colour-dark-brown text-weight-semibold">Audit report</div>
                    <div className="text-size-xsmall text-colour-alt-grey">Last run: {formatDateOnly(displayResult.fetchedAt)}</div>
                  </div>
                  <div className="line-divider is-horizontal"></div>
                  <div className="audit-report-content">
                    <div className="text-size-regular text-weight-medium">
                      Score overview:
                    </div>
                    <div className="spacer-xxsmall"></div>
                    <div className="spacer-xtiny"></div>
                    <div className="high-priority-item-grid">
                      {reportRows.map((row, index) => (
                        <div key={row.key}>
                          <div className="high-priority-audit-item-row">
                            <div className={`embed-icon ${row.verdict === 'pass' ? 'is-green-checkmark' : 'is-red-x'} w-embed`}>
                              {row.verdict === 'pass' ? CHECK_ICON : X_ICON}
                            </div>
                            <div className="text-size-small">{row.title}</div>
                          </div>
                          {index < reportRows.length - 1 ? <div className="row-divider"></div> : null}
                        </div>
                      ))}
                    </div>
                    <div className="spacer-xsmall"></div>
                    <div className="results-block">
                      <div className="result-score-item">
                        <div><span className="audit-number-pill">{passCount}</span> good</div>
                      </div>
                      <div className="result-score-item is-middle">
                        <div><span className="audit-number-pill is-orange">{priorityRecommendationCount}</span> priority</div>
                      </div>
                      <div className="result-score-item is-urgent">
                        <div><span className="audit-number-pill is-red">{urgentRecommendationCount}</span> urgent</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div ref={breakdownRef} className="dashboard-card audit-results-card">
            <div className="dashboard-card-header is-taller">
              <div className="table-title-wrapper is-left">
                <div className="dashboard-panel-title">
                  Recommendations <span className="number-pill">{recommendationRows.length}</span>
                </div>
                <InfoTooltip text="Prioritised fixes and improvements based on the current audit results." />
              </div>
            </div>
            <table className="table-component audit-results-table">
              <thead className="table-header">
                <tr className="table-row is-header-row">
                  <th className="table-head is-criteria">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Criteria</div>
                  </th>
                  <th className="table-head is-severity">
                    <div className="text-size-small text-weight-semibold text-colour-alt-grey">Priority</div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {recommendationRows.length === 0 ? (
                  <tr className="table-row">
                    <td className="table-cell is-criteria">
                      <div className="text-size-small text-weight-medium">No recommendations right now.</div>
                    </td>
                    <td className="table-cell is-severity">
                      <div className="severity-label is-pass">
                        <div>Clear</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  recommendationRows.map((row) => (
                    <tr key={`recommendation-${row.key}`} className="table-row">
                      <td
                        className="table-cell is-criteria is-clickable"
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRecommendation(row)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedRecommendation(row);
                          }
                        }}
                      >
                        <div className="text-size-small text-weight-medium">{row.title}</div>
                        <button
                          type="button"
                          className="button is-tiny is-white has-inner-shadow w-button"
                          onClick={() => setSelectedRecommendation(row)}
                        >
                          Learn more
                        </button>
                      </td>
                      <td className="table-cell is-severity">
                        <div className={getRecommendationPriorityClassName(row)}>
                          <div>{getRecommendationPriorityText(row)}</div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="spacer-small"></div>

          {groupedRows.map((group, groupIndex) => (
            <div key={group.title}>
              <div className="dashboard-card audit-results-card">
                <div className="dashboard-card-header is-taller">
                  <div className="table-title-wrapper is-left">
                    <div className="dashboard-panel-title">{group.title}</div>
                    <InfoTooltip text={group.tooltip} />
                  </div>
                </div>
                <table className="table-component audit-results-table">
                  <thead className="table-header">
                    <tr className="table-row is-header-row">
                      <th className="table-head is-criteria">
                        <div className="text-size-small text-weight-semibold text-colour-alt-grey">Criteria</div>
                      </th>
                      <th className="table-head is-severity">
                        <div className="text-size-small text-weight-semibold text-colour-alt-grey">Result</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {group.rows.map((row) => (
                      <tr
                        key={`${group.title}-${row.key}`}
                        className="table-row"
                        ref={(element) => {
                          sectionRefs.current[row.key] = element;
                        }}
                      >
                        <td className="table-cell is-criteria">
                          <div className="text-size-small text-weight-medium">{row.title}</div>
                          {row.verdict === 'partial' ? (
                            <button
                              type="button"
                              className="button is-tiny is-white has-inner-shadow w-button"
                              onClick={() => setSelectedRecommendation(createSectionDetailRow(row))}
                            >
                              Learn more
                            </button>
                          ) : null}
                        </td>
                        <td className="table-cell is-severity">
                          {renderVerdictPill(row.verdict)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {groupIndex < groupedRows.length - 1 ? <div className="spacer-small"></div> : null}
            </div>
          ))}
        </div>
      )}

      <div className="spacer-large"></div>

      <AuditIssueDetailModal
        item={selectedRecommendationItem}
        onClose={() => setSelectedRecommendation(null)}
      />
    </div>
  );
}
